import { Segmented, Select } from 'antd';
import {
  FARM_DIAGNOSTICS_TIME_RANGE_ALL,
  FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM,
  FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM_END_LABEL,
  FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM_START_LABEL,
  FARM_DIAGNOSTICS_TIME_RANGE_LABELS,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
} from 'constants/uiConstants';
import React, { useMemo } from 'react';
import type {
  FarmDiagnosticsTimeRange,
  FarmDiagnosticsTimeRangeMode,
} from '../../../../redux/slices/InsightsSlice';

const TIME_RANGE_OPTIONS: { value: FarmDiagnosticsTimeRangeMode; label: string }[] =
  [
    FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
    FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
    FARM_DIAGNOSTICS_TIME_RANGE_ALL,
    FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM,
  ].map((value) => ({ value, label: FARM_DIAGNOSTICS_TIME_RANGE_LABELS[value] }));

interface FarmDiagnosticsTimeRangeControlProps {
  /** Bounds for the custom-range year pickers — the full (unfiltered) data years in scope. */
  minYear: number;
  maxYear: number;
  value: FarmDiagnosticsTimeRange;
  onChange: (value: FarmDiagnosticsTimeRange) => void;
}

/**
 * Reusable time-range selector — a pure controlled component (no Redux of
 * its own) so independent callers can each own their own state: the three
 * per-paddock lenses share `farmDiagnosticsTimeRange`, while the Rotation
 * Efficiency lens owns a separate `rotationTimeRange`, so switching between
 * a per-paddock trend and a Farm/Org comparison never cross-talks. Switching
 * away from Custom never clears the caller's stored start/end years, so
 * picking Custom again restores the prior selection (the caller's own state
 * object is simply left untouched on `customStartYear`/`customEndYear` when
 * only `mode` changes elsewhere).
 */
const FarmDiagnosticsTimeRangeControl: React.FC<
  FarmDiagnosticsTimeRangeControlProps
> = ({ minYear, maxYear, value: timeRange, onChange }) => {
  const yearOptions = useMemo(() => {
    const options: { value: number; label: string }[] = [];
    for (let year = minYear; year <= maxYear; year++) {
      options.push({ value: year, label: String(year) });
    }
    return options;
  }, [minYear, maxYear]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Segmented<FarmDiagnosticsTimeRangeMode>
        options={TIME_RANGE_OPTIONS}
        value={timeRange.mode}
        onChange={(mode) => onChange({ ...timeRange, mode })}
      />
      {timeRange.mode === FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-muted">
            {FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM_START_LABEL}
          </span>
          <Select
            size="small"
            className="w-24"
            value={timeRange.customStartYear ?? undefined}
            placeholder={String(minYear)}
            options={yearOptions}
            onChange={(year) => onChange({ ...timeRange, customStartYear: year })}
          />
          <span className="text-sm text-gray-muted">
            {FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM_END_LABEL}
          </span>
          <Select
            size="small"
            className="w-24"
            value={timeRange.customEndYear ?? undefined}
            placeholder={String(maxYear)}
            options={yearOptions}
            onChange={(year) => onChange({ ...timeRange, customEndYear: year })}
          />
        </div>
      )}
    </div>
  );
};

export default FarmDiagnosticsTimeRangeControl;
